import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Card, Select, Switch, Tag } from 'antd';
import { TrInputNumber as InputNumber } from '../components/TrInputNumber';
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined, BankOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  fetchCashRegisters,
  createCashRegister,
  updateCashRegister,
  deleteCashRegister,
  fetchBankAccounts,
  transferToBank,
  type CashRegisterDto,
  type BankAccountDto} from '../services/cashService';
import { api } from '../services/api';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCanDelete } from '../hooks/useCanDelete';

interface Branch {
  id: number;
  branchName: string;
}

const CashRegisters: React.FC = () => {
  const isMobile = useBreakpoint();
  const canDelete = useCanDelete();
  const navigate = useNavigate();
  const [cashRegisters, setCashRegisters] = useState<CashRegisterDto[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRegister, setEditingRegister] = useState<CashRegisterDto | null>(null);
  const [form] = Form.useForm();
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferRegister, setTransferRegister] = useState<CashRegisterDto | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccountDto[]>([]);
  const [transferForm] = Form.useForm();
  const [transferSubmitting, setTransferSubmitting] = useState(false);

  useEffect(() => {
    loadCashRegisters();
    loadBranches();
    loadBankAccounts();
  }, []);

  const loadBankAccounts = async () => {
    try {
      const data = await fetchBankAccounts();
      setBankAccounts(data.filter((ba) => ba.isActive));
    } catch (error) {
      console.error('Banka hesapları yüklenirken hata:', error);
    }
  };

  const loadCashRegisters = async () => {
    try {
      setLoading(true);
      const data = await fetchCashRegisters();
      setCashRegisters(data);
    } catch (error) {
      message.error('Kasa verileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const { data } = await api.get<Branch[]>('/branches');
      setBranches(data);
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
    }
  };

  const handleAdd = () => {
    setEditingRegister(null);
    form.resetFields();
    form.setFieldsValue({ currency: 'TRY', isDefault: false, isActive: true });
    setModalVisible(true);
  };

  const handleEdit = (record: CashRegisterDto) => {
    setEditingRegister(record);
    form.setFieldsValue({
      registerCode: record.registerCode,
      registerName: record.registerName,
      branchId: record.branchId,
      openingBalance: record.openingBalance,
      currency: record.currency,
      isDefault: record.isDefault,
      isActive: record.isActive});
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCashRegister(id);
      message.success('Kasa başarıyla silindi');
      loadCashRegisters();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Kasa silinirken hata oluştu');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingRegister) {
        await updateCashRegister(editingRegister.id, values);
        message.success('Kasa başarıyla güncellendi');
      } else {
        await createCashRegister(values);
        message.success('Kasa başarıyla eklendi');
      }
      
      setModalVisible(false);
      form.resetFields();
      loadCashRegisters();
    } catch (error: any) {
      if (error.errorFields) {
        return; // Form validation error
      }
      message.error(error.response?.data?.message || 'Kasa kaydedilirken hata oluştu');
    }
  };

  const handleTransferClick = (record: CashRegisterDto) => {
    setTransferRegister(record);
    transferForm.resetFields();
    setTransferModalVisible(true);
  };

  const handleTransferSubmit = async () => {
    if (!transferRegister) return;
    try {
      const values = await transferForm.validateFields();
      setTransferSubmitting(true);
      await transferToBank(transferRegister.id, {
        bankAccountId: values.bankAccountId,
        amount: values.amount,
        description: values.description});
      message.success('Virman işlemi başarıyla kaydedildi.');
      setTransferModalVisible(false);
      transferForm.resetFields();
      setTransferRegister(null);
      loadCashRegisters();
      loadBankAccounts();
    } catch (error: any) {
      if (error.errorFields) return;
      message.error(error.response?.data?.message || 'Virman işlemi sırasında hata oluştu.');
    } finally {
      setTransferSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Kasa Kodu',
      dataIndex: 'registerCode',
      key: 'registerCode'},
    {
      title: 'Kasa Adı',
      dataIndex: 'registerName',
      key: 'registerName'},
    {
      title: 'Şube',
      dataIndex: 'branchId',
      key: 'branchId',
      render: (branchId: number) => {
        const branch = branches.find(b => b.id === branchId);
        return branch ? branch.branchName : '-';
      }},
    {
      title: 'Açılış Bakiyesi',
      dataIndex: 'openingBalance',
      key: 'openingBalance',
      render: (amount: number, record: CashRegisterDto) => (
        <span>{amount.toFixed(2)} {record.currency}</span>
      )},
    {
      title: 'Güncel Bakiye',
      dataIndex: 'currentBalance',
      key: 'currentBalance',
      render: (amount: number, record: CashRegisterDto) => (
        <Tag color={amount >= 0 ? 'green' : 'red'}>
          {amount.toFixed(2)} {record.currency}
        </Tag>
      )},
    {
      title: 'Varsayılan',
      dataIndex: 'isDefault',
      key: 'isDefault',
      render: (isDefault: boolean) => (
        <Tag color={isDefault ? 'blue' : 'default'}>
          {isDefault ? 'Evet' : 'Hayır'}
        </Tag>
      )},
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      )},
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: CashRegisterDto) => (
        <Space>
          <Button
            type="default"
            size="small"
            icon={<UnorderedListOutlined />}
            onClick={() => navigate(`/cash-registers/${record.id}/movements`)}
          >
            Hareketler
          </Button>
          <Button
            type="default"
            size="small"
            icon={<BankOutlined />}
            onClick={() => handleTransferClick(record)}
          >
            Bankaya Aktar
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          {canDelete && (
          <Popconfirm
            title="Bu kasayı silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
          )}
        </Space>
      )},
  ];

  return (
    <div>
      <Card
        title="Kasa Yönetimi"
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Yeni Kasa Ekle
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={cashRegisters}
          rowKey="id"
          loading={loading}
          scroll={{ x: 700 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Toplam ${total} kayıt`}}
        />
      </Card>

      <Modal
        title={editingRegister ? 'Kasayı Düzenle' : 'Yeni Kasa Ekle'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="registerCode"
            label="Kasa Kodu"
            rules={[{ required: true, message: 'Lütfen kasa kodu girin' }]}
          >
            <Input placeholder="KASA001" />
          </Form.Item>

          <Form.Item
            name="registerName"
            label="Kasa Adı"
            rules={[{ required: true, message: 'Lütfen kasa adı girin' }]}
          >
            <Input placeholder="Ana Kasa" />
          </Form.Item>

          <Form.Item
            name="branchId"
            label="Şube"
          >
            <Select
              placeholder="Şube seçin (opsiyonel)"
              allowClear
            >
              {branches.map(branch => (
                <Select.Option key={branch.id} value={branch.id}>
                  {branch.branchName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="openingBalance"
            label="Açılış Bakiyesi"
            rules={[{ required: true, message: 'Lütfen açılış bakiyesi girin' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.00"
              min={0}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            name="currency"
            label="Para Birimi"
            rules={[{ required: true, message: 'Lütfen para birimi seçin' }]}
          >
            <Select>
              <Select.Option value="TRY">TRY</Select.Option>
              <Select.Option value="USD">USD</Select.Option>
              <Select.Option value="EUR">EUR</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="isDefault"
            label="Varsayılan Kasa"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Aktif"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Bankaya Aktar (Virman)"
        open={transferModalVisible}
        onOk={handleTransferSubmit}
        onCancel={() => {
          setTransferModalVisible(false);
          transferForm.resetFields();
          setTransferRegister(null);
        }}
        okText="Onayla"
        cancelText="İptal"
        confirmLoading={transferSubmitting}
        width={isMobile ? '100%' : 480}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        {transferRegister && (
          <p style={{ marginBottom: 16 }}>
            <strong>Kasa:</strong> {transferRegister.registerName} — Bakiye:{' '}
            <Tag color="green">
              {Number(transferRegister.currentBalance).toFixed(2)} {transferRegister.currency}
            </Tag>
          </p>
        )}
        <Form form={transferForm} layout="vertical">
          <Form.Item
            name="amount"
            label="Miktar"
            rules={[
              { required: true, message: 'Miktar girin' },
              {
                validator: (_, value) => {
                  if (transferRegister && value > Number(transferRegister.currentBalance)) {
                    return Promise.reject(new Error('Kasa bakiyesinden fazla transfer yapılamaz'));
                  }
                  if (value != null && value <= 0) {
                    return Promise.reject(new Error('Miktar 0\'dan büyük olmalıdır'));
                  }
                  return Promise.resolve();
                }},
            ]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="0.00" min={0.01} precision={2} />
          </Form.Item>
          <Form.Item
            name="bankAccountId"
            label="Hedef Banka Hesabı"
            rules={[{ required: true, message: 'Lütfen hedef banka hesabı seçin' }]}
          >
            <Select placeholder="Banka hesabı seçin (Ziraat, İş Bankası vb.)">
              {bankAccounts.map((ba) => (
                <Select.Option key={ba.id} value={ba.id}>
                  {ba.bankName} – {ba.accountName} ({Number(ba.currentBalance).toFixed(2)} {ba.currency})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Açıklama (opsiyonel)">
            <Input placeholder="Örn: Haftalık nakit transferi" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CashRegisters;


